import type { CueMap } from '@vrlearning/lesson-schema';
import {
  GUIDE_CUT_SECONDS,
  GUIDE_LEAD_SECONDS,
  GUIDE_RESUME_SECONDS,
  cueStartAt,
} from '../../engine/guide.js';
import type { MixInput } from './mix.js';

/**
 * The "Voorlezen" button's player (ADR-033; voice-over design, "The headset"):
 * a step's voice-over, an Ogg Opus file the API signs like a poster's photo,
 * fetched on that URL, decoded, and played on the engine's voice bus --
 * non-positional, centred -- with the ambience ducked under it.
 *
 *   step's file ─► source ─► its own fade ─► voice bus ─► "Voorlezen" switch ─► master
 *
 * It stops on everything that ends a step's moment: a step change, the
 * teacher's pause ("eyes on me": stopped, not resumed), the teacher's
 * Voorlezen switch going off (over the switch's own 0.3 s fade), a teleport's
 * fade to black and the lesson's end. There is only ever one voice.
 *
 * **Modes.** `'button'`: the pupil's own **Voorlezen: aan / uit** (Pieter's
 * Quest test, 29 Sep 2026: "Once the Voorlezen button is pressed, all the
 * boxy text should be read automatically"). One press (the button, `V`)
 * turns it on and reads this step; from then on every step reads itself
 * once its panel shows -- after the teleport's fade, not during it -- until
 * the pupil presses again, which turns it off and stops. It is this
 * lesson's, in memory only: a new lesson, a late joiner and the next pupil
 * on a shared headset start with it off. The teacher's switch going off, or
 * a pause, stops the voice and hides the button, but the pupil's choice is
 * kept and reads again from the next step after it comes back (not in the
 * middle of a step). "↻ Nog eens" leaves the voice alone.
 *
 * `'guide'` (ADR-035, the voice-guide design's H2): the teacher's
 * "Begeleiding: stem" -- the voice leads the class, not the pupil. Each step
 * plays by itself once its panel shows and a 0.5 s lead-in has passed,
 * whatever the pupil's own on/off (kept for when the class goes back to
 * text). `onFinished(stepId)` says once per step entry that the voice has
 * been heard to its end -- or cut short by an answer (`answered`) -- never
 * after a stop, a step change, a pause or a replay; `onEnded(stepId)` says
 * every end, replays included, for the pacer. The teacher's pause stops
 * it, and 0.5 s after the class resumes it continues from the start of the
 * sentence that was cut (the step's cues); Voorlezen switched back on, or the
 * guide switched back to voice, plays the step from the start after the
 * lead-in. "↻ Nog eens" plays it again from the start. `position()` is read
 * off the audio clock, which the subtitles follow.
 *
 * **Entering the world** (Pieter, 29 Sep 2026): a lesson's player starts
 * `held`, and nothing starts by itself -- neither the guide nor Voorlezen
 * reading on -- until the pupil is in the world (`enter`: the XR session
 * started, or in a browser the scene loaded and the sound let play) and
 * WORLD_ENTRY_VOICE_SECONDS have passed; a step whose own lead-in ends later
 * keeps it. A press (the button, V, "Nog eens") is never held.
 *
 * Nothing runs per frame: it acts on a press, a step, a switch.
 */

export type VoiceMode = 'button' | 'guide';

/** `loading`: asked for, its file not in yet. */
export type VoiceState = 'idle' | 'loading' | 'playing';

/**
 * Files not had, as the preview's note counts them: `unloaded`, a fetch that
 * failed and has not worked since (it is tried again on the next press or
 * when its step begins again); `unplayable`, a file this browser cannot
 * decode (its step's button stays hidden for the lesson).
 */
export type VoiceMissing = { unloaded: number; unplayable: number };

/** What the player needs of the audio engine (engine.ts). */
export type VoiceBus = {
  voiceOutput(): { context: AudioContext; bus: AudioNode };
  setSpeaking(speaking: boolean): void;
  readonly input: MixInput;
  onInput(handler: (input: MixInput) => void): () => void;
};

export type VoiceOptions = {
  /** Media id -> signed URL, as the lesson came with it. */
  media: Readonly<Record<string, string>>;
  /** A file's bytes, through the service worker's media cache. */
  fetchFile: (url: string) => Promise<ArrayBuffer>;
  /** The button's state changed: shown or hidden, "Voorlezen" or "Stop". */
  onChange?: (state: VoiceState) => void;
  /** The count of files not loaded or not playable here changed. */
  onMissing?: (missing: VoiceMissing) => void;
  /** `'button'` (the default), or `'guide'` for the class's voice guide; `setMode` changes it. */
  mode?: VoiceMode;
  /** Voorlezen on from the start: the demo only (`voiceStartsOn`). */
  on?: boolean;
  /** Media id -> its subtitle cues (ADR-035): where a voice cut by the pause resumes. */
  cues?: CueMap;
  /** Guide mode: the step's voice was heard to its end, or an answer cut it; once per step entry. */
  onFinished?: (stepId: string) => void;
  /**
   * Guide mode: the voice has just ended -- played to its end, or cut by an
   * answer -- every time, a replay's end and a restart's too. What the pacer
   * waits for; `onFinished` is what the class is told, once.
   */
  onEnded?: (stepId: string) => void;
  /**
   * Held until the world is entered (`enter`): the first automatic start --
   * the guide's, or Voorlezen reading by itself -- waits for it. A press does not.
   */
  held?: boolean;
  /** Runs `run` after `seconds` (the lead-in, the resume); returns a cancel. The browser's timer by default. */
  schedule?: (run: () => void, seconds: number) => () => void;
};

const browserSchedule = (run: () => void, seconds: number): (() => void) => {
  const timer = setTimeout(run, seconds * 1000);
  return () => clearTimeout(timer);
};

/** A press's fade out, and the teacher's switch's (the switch fades over 0.3 s itself). */
const STOP_SECONDS = 0.1;
const SWITCH_SECONDS = 0.3;

/**
 * Decoded voices kept at once: this step's and the last. A step's file is
 * small (32 kbps Opus), its decoded audio is not (15 s at 48 kHz is 2.9 MB),
 * so the bytes are kept for the whole lesson, and a file is decoded only when
 * the pupil first asks for it.
 */
const DECODED_KEPT = 2;

/**
 * Whether the pupil's Voorlezen starts on. Only in the demo, which is shown
 * to somebody as a whole (Pieter, 29 Sep 2026: "the lesson with audio
 * guidance as a standalone test"): after "Start in VR", which lets the sound
 * play, every step reads itself; the button switches it off. A class and a
 * played lesson start off, so a pupil chooses it.
 */
export function voiceStartsOn(kind: 'class' | 'play' | 'demo'): boolean {
  return kind === 'demo';
}

/** Whether the button shows (hidden, not disabled, otherwise; as the console hides its switch). */
export function voiceButtonShown(input: {
  /** The step has a voice-over with a signed URL that this browser has not failed to decode. */
  available: boolean;
  /** The teacher's "Voorlezen" (a played lesson and the demo count as on); undefined: not known yet. */
  voice: boolean | undefined;
  paused: boolean;
}): boolean {
  return input.available && input.voice === true && !input.paused;
}

/**
 * The teacher's switch as the button may use it: in a class, unknown until
 * the first `session.state` has said it -- a lesson pushed with Voorlezen off
 * would otherwise flash the button on. Practising alone, a played lesson and
 * the demo have no class to wait for.
 */
export function classVoice(input: {
  inClass: boolean;
  stateSeen: boolean;
  voice: boolean;
}): boolean | undefined {
  return input.inClass && !input.stateSeen ? undefined : input.voice;
}

/** The lesson's voice-overs that came with a URL, each once, in step order. */
export function voicesOf(
  steps: readonly { voiceover?: string }[],
  media: Readonly<Record<string, string>>,
): string[] {
  const ids = new Set<string>();
  for (const step of steps) {
    if (step.voiceover !== undefined && media[step.voiceover] !== undefined) {
      ids.add(step.voiceover);
    }
  }
  return [...ids];
}

type Playing = {
  source: AudioBufferSourceNode;
  fade: GainNode;
  /** The audio clock's time at the start, and the offset into the file it started at. */
  startedAt: number;
  offset: number;
};

export class VoicePlayer {
  #mode: VoiceMode;
  readonly #bus: VoiceBus;
  readonly #options: VoiceOptions;
  /** The lesson's files, fetched once each. */
  readonly #bytes = new Map<string, Promise<ArrayBuffer>>();
  /** The last few decoded, most recent last. */
  readonly #decoded = new Map<string, AudioBuffer>();
  readonly #decoding = new Map<string, Promise<AudioBuffer | undefined>>();
  /** Files this browser cannot decode: given up for the lesson. */
  readonly #undecodable = new Set<string>();
  /** Files whose fetch failed and has not worked since: tried again. */
  readonly #unloaded = new Set<string>();
  readonly #stopFollowing: () => void;
  /** The step's voice-over id, if it has one. */
  #step: string | undefined;
  #state: VoiceState = 'idle';
  #playing: Playing | undefined;
  /** The pupil's Voorlezen, for this lesson: in memory, never stored. */
  #on = false;
  /** Bumped on every step: the step whose panel may still start the voice. */
  #entry = 0;
  #waiting: number | undefined;
  /** Bumped by every stop and step: a file that arrives for an older ask is not played. */
  #ask = 0;
  #disposed = false;
  readonly #schedule: (run: () => void, seconds: number) => () => void;
  /** The class's switches as last followed: what changed is what counts. */
  #input: Pick<MixInput, 'voice' | 'paused'>;
  // --- guide mode, per step entry ---
  /** The step, for `onFinished`. */
  #stepId: string | undefined;
  /** This entry's panel has shown: the voice may play. */
  #shown = false;
  /** This reading has ended (or was cut by an answer): not resumed; a replay or restart reads anew. */
  #done = false;
  /** `onFinished` has been said for this entry (guide mode only): never twice. */
  #reported = false;
  /** Started at least once this entry (for the fallback to text when it is late). */
  #started = false;
  #starts = 0;
  /** Where to continue once the class may hear it again; undefined: nothing to continue. */
  #resumeAt: number | undefined;
  /** The lead-in or resume waiting to start the voice. */
  #cancelWait: (() => void) | undefined;
  // --- the world's entry ---
  /** Nothing starts by itself until the world has been entered and `enter`'s seconds have passed. */
  #held: boolean;
  /** The entry's wait is running. */
  #entering = false;
  /** The automatic start the hold kept back, tried again (its conditions read afresh) when it ends. */
  #heldStart: (() => void) | undefined;
  /** Where the voice stands when it is not playing: where it was cut, or 0. */
  #position = 0;
  /** Played to its end: where, and when on the audio clock; the position keeps counting from there. */
  #ended: { position: number; at: number } | undefined;

  constructor(bus: VoiceBus, options: VoiceOptions) {
    this.#bus = bus;
    this.#options = options;
    this.#mode = options.mode ?? 'button';
    this.#on = options.on === true;
    this.#schedule = options.schedule ?? browserSchedule;
    this.#held = options.held === true;
    this.#input = { voice: bus.input.voice, paused: bus.input.paused };
    this.#stopFollowing = bus.onInput((input) => this.#follow(input));
  }

  /** `'button'`: the pupil's own Voorlezen; `'guide'`: the class's voice guide. */
  get mode(): VoiceMode {
    return this.#mode;
  }

  get state(): VoiceState {
    return this.#state;
  }

  /** Guide mode: this entry's voice has begun at least once. */
  get started(): boolean {
    return this.#started;
  }

  /** A start is on its way: the lead-in or resume is running, the file is loading, or the hold keeps it. */
  get due(): boolean {
    return (
      this.#cancelWait !== undefined || this.#state === 'loading' || this.#heldStart !== undefined
    );
  }

  /** Still waiting for the world's entry (and its seconds): nothing starts by itself. */
  get held(): boolean {
    return this.#held;
  }

  /**
   * The world has been entered (XR started, or the scene loaded and the
   * sound let play): what the hold kept back starts `seconds` from now
   * (WORLD_ENTRY_VOICE_SECONDS), and anything asked for later starts as
   * ever, with its own lead-in. Once only.
   */
  enter(seconds: number): void {
    if (!this.#held || this.#entering || this.#disposed) return;
    this.#entering = true;
    this.#schedule(() => {
      if (this.#disposed) return;
      this.#held = false;
      const start = this.#heldStart;
      this.#heldStart = undefined;
      start?.();
    }, seconds);
  }

  /** An automatic start: now, or, while held, when the hold ends (the latest one asked for). */
  #autoStart(start: () => void): void {
    if (!this.#held) {
      start();
      return;
    }
    const entry = this.#entry;
    this.#heldStart = () => {
      if (entry === this.#entry && !this.#disposed) start();
    };
  }

  /** How many times a voice has begun, ever: a replay and a resume count (for the stall watch). */
  get starts(): number {
    return this.#starts;
  }

  /**
   * Seconds into the step's voice: the audio clock while it plays
   * (`currentTime − startedAt + offset`), where it was cut otherwise.
   */
  position(): number {
    const playing = this.#playing;
    const ended = this.#ended;
    if (!playing && !ended) return this.#position;
    const { context } = this.#bus.voiceOutput();
    if (playing) return context.currentTime - playing.startedAt + playing.offset;
    return ended!.position + (context.currentTime - ended!.at);
  }

  /**
   * The guide switched (decision 8): to text, the voice stops over 0.3 s and
   * the pupil's own Voorlezen applies again from the next step; to voice, the
   * step plays from the start after the lead-in.
   */
  setMode(mode: VoiceMode): void {
    if (mode === this.#mode || this.#disposed) return;
    this.#mode = mode;
    this.#wait(undefined);
    this.#waiting = undefined;
    this.#heldStart = undefined;
    if (mode === 'button') {
      this.stop(GUIDE_CUT_SECONDS);
      return;
    }
    this.stop(GUIDE_CUT_SECONDS);
    this.#fromStart();
    this.#playWhenAllowed(GUIDE_LEAD_SECONDS);
  }

  /** The pupil has Voorlezen on: each step reads itself. */
  get on(): boolean {
    return this.#on;
  }

  /** The step has a voice-over this headset can play, as far as it knows. */
  get available(): boolean {
    const id = this.#step;
    return (
      !this.#disposed &&
      id !== undefined &&
      this.#options.media[id] !== undefined &&
      !this.#undecodable.has(id)
    );
  }

  /**
   * Fetches every step's file at the lesson's start, one after another, so
   * the service worker has them for a classroom without Wi-Fi. Not decoded.
   */
  prefetch(ids: readonly string[]): void {
    void (async () => {
      for (const id of ids) {
        if (this.#disposed) return;
        if (this.#options.media[id] === undefined) continue;
        try {
          await this.#fetch(id);
        } catch {
          // Counted as not loaded (#fetch); tried again when its step begins.
        }
      }
    })();
  }

  /**
   * A new step: whatever speaks stops, and this step's file is fetched if it
   * is not in yet (or failed before). Decoded only when asked for. Returns
   * what to call once the step's panel shows (after the teleport's fade):
   * with Voorlezen on, and the class's switch on and not paused, the step
   * reads itself then. A later step's call makes an earlier one do nothing.
   */
  stepChanged(voiceover: string | undefined, stepId?: string): () => void {
    this.stop();
    this.#wait(undefined);
    this.#heldStart = undefined;
    this.#step = voiceover;
    this.#stepId = stepId;
    this.#shown = false;
    this.#done = false;
    this.#reported = false;
    this.#started = false;
    this.#resumeAt = 0;
    this.#position = 0;
    this.#ended = undefined;
    if (this.available) this.#fetch(voiceover!).catch(() => {});
    const entry = ++this.#entry;
    const { voice, paused } = this.#bus.input;
    this.#waiting = this.#on && voice && !paused ? entry : undefined;
    this.#tell();
    return () => {
      if (entry !== this.#entry || this.#disposed) return;
      this.#shown = true;
      if (this.#mode === 'guide') {
        this.#waiting = undefined;
        this.#playWhenAllowed(GUIDE_LEAD_SECONDS);
        return;
      }
      if (this.#waiting !== entry) return;
      this.#waiting = undefined;
      if (!this.#on) return;
      this.#autoStart(() => {
        // Read afresh when a hold kept it: the pupil or the class may have changed their mind.
        const { voice: on, paused: still } = this.#bus.input;
        if (this.#mode === 'button' && this.#on && on && !still) this.play();
      });
    };
  }

  /**
   * "↻ Nog eens": in button mode the voice is left alone; in guide mode the
   * step's voice plays again from the start (and does not say it has
   * finished a second time).
   */
  replayed(): void {
    if (this.#mode !== 'guide' || !this.#shown) return;
    this.#wait(undefined);
    this.#heldStart = undefined;
    this.#fromStart();
    const { voice, paused } = this.#bus.input;
    if (voice && !paused) this.#playFrom(0);
  }

  /**
   * Guide mode: an answer given while the voice reads stops it over 0.3 s,
   * and that counts as having listened (`onFinished`). True when it cut
   * something short; false when the voice had finished, or never began.
   */
  answered(): boolean {
    if (this.#mode !== 'guide' || this.#done) return false;
    const speaking = this.#state !== 'idle' || this.#cancelWait !== undefined;
    if (!speaking && !this.#started) return false;
    this.#wait(undefined);
    this.stop(GUIDE_CUT_SECONDS);
    this.#finish();
    return true;
  }

  /** Restarts the step's decoded voice `seconds` in (the resume after a pause). */
  resumeFrom(seconds: number): void {
    this.#playFrom(seconds);
  }

  /** The button, and `V`: Voorlezen on (and this step read now), or off (and stopped). */
  toggle(): void {
    if (this.#disposed) return;
    this.#on = !this.#on;
    this.#waiting = undefined;
    this.#heldStart = undefined;
    if (this.#on) this.play();
    else this.stop();
    this.#tell();
  }

  /** From the start; whatever spoke before stops first. */
  play(): void {
    this.#playFrom(0);
  }

  #playFrom(offset: number): void {
    if (!this.available) return;
    this.#halt(0);
    const id = this.#step!;
    const ask = ++this.#ask;
    const ready = this.#decoded.get(id);
    if (ready) {
      this.#start(ready, offset);
      return;
    }
    this.#set('loading');
    void this.#decode(id).then((buffer) => {
      if (ask !== this.#ask || this.#disposed) return;
      if (buffer) this.#start(buffer, offset);
      else this.#set('idle');
    });
  }

  /** Stops with a short fade (a teleport's fade to black, a press, a step). */
  stop(seconds = STOP_SECONDS): void {
    this.#ask++;
    if (this.#playing) this.#position = this.position();
    this.#wait(undefined);
    this.#halt(seconds);
    this.#set('idle');
  }

  /** Guide mode: plays from `#resumeAt` after `seconds`, when the class may hear it (and the hold lets it). */
  #playWhenAllowed(seconds: number): void {
    if (!this.#allowed()) return;
    const from = this.#resumeAt!;
    this.#wait(() => {
      if (!this.#held) {
        this.#playFrom(from);
        return;
      }
      this.#autoStart(() => {
        if (this.#allowed()) this.#playFrom(this.#resumeAt!);
      });
    }, seconds);
  }

  /** Guide mode, the panel shown, the class hearing, and something to play. */
  #allowed(): boolean {
    const { voice, paused } = this.#bus.input;
    return (
      this.#mode === 'guide' && this.#shown && voice && !paused && this.#resumeAt !== undefined
    );
  }

  /** The one timer that may start the voice: replaced, or cancelled with `undefined`. */
  #wait(run: (() => void) | undefined, seconds = 0): void {
    this.#cancelWait?.();
    this.#cancelWait = undefined;
    if (!run) return;
    const entry = this.#entry;
    const cancel = this.#schedule(() => {
      if (this.#cancelWait !== cancel || entry !== this.#entry || this.#disposed) return;
      this.#cancelWait = undefined;
      run();
    }, seconds);
    this.#cancelWait = cancel;
  }

  /** The step is read again from its start: a replay, the guide or Voorlezen coming back. */
  #fromStart(): void {
    this.#resumeAt = 0;
    this.#done = false;
  }

  /**
   * Heard to its end, or cut by an answer. Guide mode says so to the pacer
   * every time (`onEnded`), and to the class once per entry (`onFinished`) --
   * a reading heard in button mode is not a guided one, so it says nothing.
   */
  #finish(): void {
    if (this.#done) return;
    this.#done = true;
    this.#resumeAt = undefined;
    const stepId = this.#stepId;
    if (this.#mode !== 'guide' || stepId === undefined || this.#disposed) return;
    this.#options.onEnded?.(stepId);
    if (this.#reported) return;
    this.#reported = true;
    this.#options.onFinished?.(stepId);
  }

  /** The lesson has gone: silent, nothing kept, the engine no longer followed. */
  dispose(): void {
    this.stop();
    this.#wait(undefined);
    this.#disposed = true;
    this.#stopFollowing();
    this.#bytes.clear();
    this.#decoded.clear();
    this.#decoding.clear();
  }

  #report(): void {
    if (this.#disposed) return;
    this.#options.onMissing?.({
      unloaded: this.#unloaded.size,
      unplayable: this.#undecodable.size,
    });
  }

  /**
   * The teacher's switch off, or a pause: the voice stops, the pupil's
   * choice is kept for the next step. In guide mode the voice also comes
   * back: 0.5 s after a pause, from the start of the sentence it cut; from
   * the step's start, after the lead-in, when Voorlezen comes back on.
   */
  #follow(input: MixInput): void {
    const before = this.#input;
    this.#input = { voice: input.voice, paused: input.paused };
    if (this.#mode === 'guide') {
      this.#guideFollow(before, input);
      return;
    }
    if (input.voice && !input.paused) return;
    this.#waiting = undefined;
    // Kept back by the hold: not read in the middle of a step once it comes back.
    this.#heldStart = undefined;
    if (this.#state === 'idle') return;
    if (!input.voice) this.stop(SWITCH_SECONDS);
    else this.stop();
  }

  #guideFollow(before: Pick<MixInput, 'voice' | 'paused'>, input: MixInput): void {
    if (!input.voice) {
      if (!before.voice) return;
      // Off: stopped over the switch's fade; back on, the step from its start.
      this.stop(SWITCH_SECONDS);
      this.#fromStart();
      return;
    }
    if (input.paused) {
      if (before.paused) return;
      // "Eyes on me": stopped, and continued from the sentence it cut.
      const waiting = this.#cancelWait !== undefined;
      const speaking = this.#state === 'playing';
      if (speaking) {
        const at = cueStartAt(this.#cues(), this.position());
        this.stop();
        if (at === undefined) this.#finish();
        else this.#resumeAt = at;
      } else if (this.#state === 'loading' || waiting) {
        this.stop();
      }
      return;
    }
    if (!before.voice) {
      this.#fromStart();
      this.#playWhenAllowed(GUIDE_LEAD_SECONDS);
    } else if (before.paused && !this.#done) {
      this.#playWhenAllowed(GUIDE_RESUME_SECONDS);
    }
  }

  /** The step's cues, if it has any. */
  #cues(): readonly { start: number; end: number }[] {
    const id = this.#step;
    return (id === undefined ? undefined : this.#options.cues?.[id]) ?? [];
  }

  /** A file's bytes, fetched once; a failed fetch is forgotten, so the next ask tries again. */
  #fetch(id: string): Promise<ArrayBuffer> {
    let bytes = this.#bytes.get(id);
    if (!bytes) {
      bytes = this.#options.fetchFile(this.#options.media[id]!).then(
        (loaded) => {
          if (this.#unloaded.delete(id)) this.#report();
          return loaded;
        },
        (error: unknown) => {
          this.#bytes.delete(id);
          if (!this.#unloaded.has(id)) {
            this.#unloaded.add(id);
            this.#report();
          }
          throw error;
        },
      );
      this.#bytes.set(id, bytes);
    }
    return bytes;
  }

  /** The step's audio, decoded once while kept; undefined when it could not be had or decoded. */
  #decode(id: string): Promise<AudioBuffer | undefined> {
    const kept = this.#decoded.get(id);
    if (kept) return Promise.resolve(kept);
    const pending = this.#decoding.get(id);
    if (pending) return pending;
    const decoding = (async (): Promise<AudioBuffer | undefined> => {
      let bytes: ArrayBuffer;
      try {
        bytes = await this.#fetch(id);
      } catch {
        this.#decoding.delete(id);
        return undefined;
      }
      try {
        const { context } = this.#bus.voiceOutput();
        // A copy: decoding detaches what it is given, and the bytes are kept.
        const buffer = await context.decodeAudioData(bytes.slice(0));
        if (this.#disposed) return undefined;
        this.#keep(id, buffer);
        return buffer;
      } catch {
        this.#giveUp(id);
        return undefined;
      } finally {
        this.#decoding.delete(id);
      }
    })();
    this.#decoding.set(id, decoding);
    return decoding;
  }

  #keep(id: string, buffer: AudioBuffer): void {
    this.#decoded.delete(id);
    this.#decoded.set(id, buffer);
    while (this.#decoded.size > DECODED_KEPT) {
      const oldest = this.#decoded.keys().next().value!;
      this.#decoded.delete(oldest);
    }
  }

  /** A file this browser cannot decode: its step's button hides for the lesson, said once. */
  #giveUp(id: string): void {
    if (this.#disposed || this.#undecodable.has(id)) return;
    this.#undecodable.add(id);
    this.#report();
    if (id === this.#step) this.#tell();
  }

  #start(buffer: AudioBuffer, offset = 0): void {
    const { context, bus } = this.#bus.voiceOutput();
    const fade = context.createGain();
    fade.connect(bus);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(fade);
    const at = Math.max(0, Math.min(offset, buffer.duration));
    const playing: Playing = { source, fade, startedAt: context.currentTime, offset: at };
    source.onended = () => {
      fade.disconnect();
      // A stop's own `ended` is not the voice finishing: `#halt` let go of it first.
      if (this.#playing !== playing) return;
      this.#playing = undefined;
      const now = context.currentTime;
      this.#ended = { position: now - playing.startedAt + playing.offset, at: now };
      this.#resumeAt = undefined;
      this.#bus.setSpeaking(false);
      this.#finish();
      this.#set('idle');
    };
    if (at > 0) source.start(0, at);
    else source.start();
    this.#started = true;
    this.#starts++;
    this.#done = false;
    this.#ended = undefined;
    this.#playing = playing;
    this.#bus.setSpeaking(true);
    this.#set('playing');
  }

  /** Takes the voice down (0 s: at once, for the next one), and lets the ambience back. */
  #halt(seconds: number): void {
    const playing = this.#playing;
    if (!playing) return;
    this.#playing = undefined;
    const { context } = this.#bus.voiceOutput();
    const now = context.currentTime;
    const gain = playing.fade.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.linearRampToValueAtTime(0, now + seconds);
    playing.source.stop(now + seconds);
    this.#bus.setSpeaking(false);
  }

  #set(state: VoiceState): void {
    if (state === this.#state) return;
    this.#state = state;
    this.#tell();
  }

  #tell(): void {
    if (!this.#disposed) this.#options.onChange?.(this.#state);
  }
}
